import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { formatDate } from '../../util/datetime';

import releases from '../../data/releaseNotes.json';

/**
 * Help → "What's new". Renders the platform release notes that the
 * build step (scripts/gen-release-notes.mjs) pulls from GitHub Releases
 * into src/data/releaseNotes.json. No runtime fetch — the data is baked
 * into the bundle at build time, so the authenticated portal makes no
 * external call and ships no token.
 *
 * Release bodies are GitHub-flavoured markdown. Rather than pull in a
 * full markdown dependency for a handful of notes, we render the common
 * subset the notes actually use — headings, bullet lists, and
 * paragraphs — line by line. Everything is plain text (no HTML
 * injection), so there's no XSS surface.
 */

interface Release {
  tag: string;
  name: string;
  date: string | null;
  url: string;
  prerelease: boolean;
  body: string;
}

const fmtDate = (iso: string | null): string => formatDate(iso);

/** Strip a leading inline-markdown emphasis so '**Bold**' reads as
 *  'Bold' without pulling in a markdown parser. */
const stripEmphasis = (s: string): string =>
  s.replace(/\*\*(.+?)\*\*/g, '$1').replace(/`(.+?)`/g, '$1');

/** Minimal line-based renderer for the heading / bullet / paragraph
 *  subset our release notes use. */
function ReleaseBody({ body }: { body: string }) {
  const lines = body.split('\n');
  const blocks: React.ReactNode[] = [];
  let bullets: string[] = [];

  const flushBullets = () => {
    if (bullets.length === 0) return;
    const items = bullets;
    bullets = [];
    blocks.push(
      <Box
        component="ul"
        key={`ul-${blocks.length}`}
        sx={{ my: 0.5, pl: 3 }}
      >
        {items.map((b, i) => (
          <li key={i}>
            <Typography variant="body2">{stripEmphasis(b)}</Typography>
          </li>
        ))}
      </Box>,
    );
  };

  lines.forEach((raw) => {
    const line = raw.trimEnd();
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (heading) {
      flushBullets();
      blocks.push(
        <Typography
          key={`h-${blocks.length}`}
          variant="subtitle1"
          sx={{ fontWeight: 600, mt: 1 }}
        >
          {stripEmphasis(heading[2])}
        </Typography>,
      );
    } else if (bullet) {
      bullets.push(bullet[1]);
    } else if (line.trim() === '') {
      flushBullets();
    } else {
      flushBullets();
      blocks.push(
        <Typography key={`p-${blocks.length}`} variant="body2" sx={{ mt: 0.5 }}>
          {stripEmphasis(line)}
        </Typography>,
      );
    }
  });
  flushBullets();
  return <>{blocks}</>;
}

export function ReleaseNotes() {
  const list = releases as Release[];

  return (
    <Paper sx={{ p: 3, maxHeight: '70vh', overflow: 'auto' }}>
      <Stack spacing={1}>
        <Typography variant="h5">What's new</Typography>
        <Typography color="text.secondary" variant="body2">
          Release notes for the platform, newest first. Assembled from
          our published GitHub releases at build time.
        </Typography>
      </Stack>

      {list.length === 0 ? (
        <Typography color="text.disabled" sx={{ mt: 3 }}>
          No release notes published yet.
        </Typography>
      ) : (
        <Stack spacing={2} sx={{ mt: 2 }} divider={<Divider flexItem />}>
          {list.map((r) => (
            <Box key={r.tag}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="baseline"
                flexWrap="wrap"
              >
                <Typography variant="h6">{r.name}</Typography>
                {r.prerelease && (
                  <Chip size="small" label="pre-release" color="warning" />
                )}
                {r.date && (
                  <Typography variant="caption" color="text.secondary">
                    {fmtDate(r.date)}
                  </Typography>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Link
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                >
                  {r.tag}
                </Link>
              </Stack>
              <Box sx={{ mt: 1 }}>
                <ReleaseBody body={r.body} />
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
